import React, { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";

import { formatStringByDraftType } from "../../utils/string";

import { DRAFT_TYPES, DRAFT_TYPE_OPTIONS } from "../../globals/static-data";

import DraftFields from "./parts";

import { updateLocalStorageData } from "../../utils/localStorage";

import {
  Container,
  DraftAreaContainer,
  TypeContainer,
  LineNumbers,
  Line,
  EditAndDeleteButton,
  CopyIcon,
  CheckIcon,
  InnerContainer,
  TrashCanIcon,
  EditAndDeleteButtonsContainer,
  ErrorText,
  CreatedAtContainer,
  DownloadIcon,
} from "./styles";

const DraftBox = ({
  title,
  draftContent,
  type,
  id,
  deleteDraft,
  fetchDrafts,
  createdAt,
}) => {
  const [content, setContent] = useState(draftContent);
  const [contentErrorMessage, setContentErrorMessage] = useState("");
  const [contentType, setContentType] = useState(type);
  const [copied, setCopied] = useState(false);

  const methods = useForm({
    mode: "onChange",
  });

  const { setValue, watch } = methods;

  const handleJsonChange = (event) => {
    setContent(event.target.value);
  };

  const handleJsonChangeAndBeautify = (event) => {
    setContent(formatStringByDraftType(event.target.value, contentType.id));
    setValue(
      "content",
      formatStringByDraftType(event.target.value, contentType.id)
    );
  };

  const formatContent = (content) => {
    return formatStringByDraftType(content, contentType.id);
  };

  const handleDraftTypeChange = (type) => {
    setContentType(type);
    setValue("content", formatStringByDraftType(content, type.id));
    setContent(formatStringByDraftType(content, type.id));
  };

  useEffect(() => {
    handleDraftTypeChange(type);
  }, [type]);

  const [lines, setLines] = useState(1);
  const textareaRef = useRef(null);
  const lineNumberRef = useRef(null);

  useEffect(() => {
    const lineCount = content.split("\n")?.length;
    setLines(lineCount);
  }, [content]);

  // check if content is valid for visualization
  useEffect(() => {
    switch (contentType.id) {
      case DRAFT_TYPES.JSON:
        try {
          JSON.parse(content);
          setContentErrorMessage("");
        } catch (error) {
          setContentErrorMessage(
            ` ${contentType.description} visualization is not available because data is not valid`
          );
        }
        break;
      default:
        setContentErrorMessage("");
    }
  }, [content, contentType]);

  const handleScroll = () => {
    if (lineNumberRef.current && textareaRef.current) {
      lineNumberRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const { handleSubmit } = methods;

  const contentValue = watch("content");
  const titleValue = watch("title");
  const typeValue = watch("type");

  useEffect(() => {
    if (titleValue || contentValue || typeValue) {
      updateLocalStorageData(id, {
        id,
        title: titleValue,
        content: contentValue,
        type: typeValue,
        createdAt: createdAt,
      });
      fetchDrafts();
    }
  }, [contentValue, titleValue, typeValue]);

  const onSubmit = (data) => {
    console.log("data ", data);
    try {
      updateLocalStorageData(id, data);
      fetchDrafts();
    } catch (error) {
      console.error("Failed to save draft:", error);
    }
  };

  const handleDelete = (id) => {
    deleteDraft(id);
  };

  const handleCopy = () => {
    if (textareaRef.current) {
      navigator.clipboard
        .writeText(textareaRef.current.value)
        .then(() => {
          setCopied(true); // Set copied state to true
          setTimeout(() => setCopied(false), 1000); // Reset copied state after 2 seconds
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }
  };

  function sanitizeFilename(title) {
    return title
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9-_]/g, "");
  }

  const handleDownload = () => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename = title ? sanitizeFilename(title) : "paste";
    a.download = `${filename}.${contentType.file}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  function formatDate(isoString) {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Container>
          <CreatedAtContainer>
            created on {formatDate(createdAt)}
          </CreatedAtContainer>
          <DraftFields
            name="title"
            placeholder="Title"
            defaultValue={title}
            id={id}
          />
          <InnerContainer>
            <TypeContainer>
              Type:
              <DraftFields
                name="type"
                type="DROPDOWN"
                onChange={handleDraftTypeChange}
                defaultValue={contentType}
                options={DRAFT_TYPE_OPTIONS}
              ></DraftFields>
            </TypeContainer>
            <EditAndDeleteButtonsContainer>
              <EditAndDeleteButton type="button" onClick={handleCopy}>
                {copied ? (
                  <>
                    <CheckIcon /> Copied!
                  </>
                ) : (
                  <>
                    <CopyIcon /> Copy
                  </>
                )}
              </EditAndDeleteButton>
              <EditAndDeleteButton
                isDelete
                type="button"
                onClick={() => handleDelete(id)}
              >
                <TrashCanIcon />
              </EditAndDeleteButton>
              <EditAndDeleteButton type="button" onClick={handleDownload}>
                <DownloadIcon /> Download
              </EditAndDeleteButton>
            </EditAndDeleteButtonsContainer>
          </InnerContainer>
          <ErrorText>{contentErrorMessage}</ErrorText>
          <DraftAreaContainer>
            <LineNumbers
              ref={lineNumberRef}
              isVisible={contentType.id === DRAFT_TYPES.JSON}
            >
              {[...Array(lines)].map((_, i) => (
                <Line key={i}>{i + 1}</Line>
              ))}
            </LineNumbers>
            <DraftFields
              name="content"
              type="TEXTAREA"
              ref={textareaRef}
              defaultValue={draftContent}
              onScroll={handleScroll}
              formatContent={formatContent}
              contentType={contentType.id}
              onFocus={handleJsonChangeAndBeautify}
              onBlur={handleJsonChangeAndBeautify}
              onChange={handleJsonChange}
            ></DraftFields>
          </DraftAreaContainer>
        </Container>
      </form>
    </FormProvider>
  );
};

export default DraftBox;
